/**
 * Format event date range with smart display logic
 *
 * Rules:
 * - Show date range if both start and end dates are specified
 * - Only show time if start is not 12:00 AM or end is not 11:59 PM
 * - If start and end dates are the same, only show date once with time range
 *
 * @param startDate - Start date/time (ISO string or Date)
 * @param endDate - End date/time (ISO string or Date)
 * @returns Formatted date/time string
 */
export function formatEventDateRange(
  startDate: string | Date | null | undefined,
  endDate?: string | Date | null | undefined
): string {
  if (!startDate) return '';

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : null;

  // Check if time is midnight (12:00 AM)
  const isMidnight = (date: Date) => {
    return date.getHours() === 0 && date.getMinutes() === 0;
  };

  // Check if time is 11:59 PM
  const isEndOfDay = (date: Date) => {
    return date.getHours() === 23 && date.getMinutes() === 59;
  };

  // Check if two dates are on the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Determine if we should show times
  const showStartTime = !isMidnight(start);
  const showEndTime = end && !isEndOfDay(end);

  // Case 1: Only start date
  if (!end) {
    if (showStartTime) {
      return `${formatDate(start)} at ${formatTime(start)}`;
    }
    return formatDate(start);
  }

  // Case 2: Start and end on same day
  if (isSameDay(start, end)) {
    const dateStr = formatDate(start);

    if (showStartTime || showEndTime) {
      const timeRange = [];
      if (showStartTime) timeRange.push(formatTime(start));
      if (showEndTime) timeRange.push(formatTime(end));

      if (timeRange.length === 2) {
        return `${dateStr}, ${timeRange[0]} - ${timeRange[1]}`;
      } else if (timeRange.length === 1) {
        return `${dateStr} at ${timeRange[0]}`;
      }
    }

    return dateStr;
  }

  // Case 3: Different days
  const startStr = showStartTime
    ? `${formatDate(start)} at ${formatTime(start)}`
    : formatDate(start);

  const endStr = showEndTime ? `${formatDate(end)} at ${formatTime(end)}` : formatDate(end);

  return `${startStr} - ${endStr}`;
}
